import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { PriceInputCell, type PriceValue } from "./PriceInputCell";

export type PriceServiceGroup = {
  serviceCategoryId: string;
  serviceLabel: string;
  /** Preço "flat" do serviço (materialOptionId null) — vale pra qualquer material, é a linha
   *  principal sempre visível. */
  flat: PriceValue;
  /** Detalhamento por material — opcional, só aparece expandido. Um preço aqui vence o flat
   *  (mesma precedência do motor de resolução: específico > "qualquer material"). */
  materials: Array<{ materialOptionId: string; materialLabel: string } & PriceValue>;
};

type Props = {
  groups: PriceServiceGroup[];
  onSave: (serviceCategoryId: string, materialOptionId: string | null, priceCents: number) => void;
  onRestore?: (serviceCategoryId: string, materialOptionId: string | null) => void;
  saving?: boolean;
};

export function PriceTable({ groups, onSave, onRestore, saving = false }: Props) {
  if (groups.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-[12.5px] text-zinc-500 dark:text-muted-foreground">
        Nenhum serviço ativo no catálogo ainda.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {groups.map((group) => (
        <ServiceGroupRow
          key={group.serviceCategoryId}
          group={group}
          onSave={onSave}
          onRestore={onRestore}
          saving={saving}
        />
      ))}
    </div>
  );
}

function ServiceGroupRow({
  group,
  onSave,
  onRestore,
  saving,
}: {
  group: PriceServiceGroup;
} & Omit<Props, "groups">) {
  const hasMaterials = group.materials.length > 0;
  const hasMaterialValue = group.materials.some((m) => m.priceCents != null);
  const [expanded, setExpanded] = useState(hasMaterialValue);

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-border">
      <div className="flex items-center gap-2 px-3 py-2.5">
        {hasMaterials ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-muted"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm text-zinc-700 dark:text-foreground/80">
          {group.serviceLabel}
        </span>
        <PriceInputCell
          {...group.flat}
          onSave={(cents) => onSave(group.serviceCategoryId, null, cents)}
          onRestore={onRestore ? () => onRestore(group.serviceCategoryId, null) : undefined}
          saving={saving}
        />
      </div>

      {hasMaterials && expanded && (
        <div className="space-y-1.5 border-t border-zinc-100 px-3 py-2.5 pl-11 dark:border-border/60">
          {group.materials.map((m) => (
            <div key={m.materialOptionId} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-zinc-500 dark:text-muted-foreground">
                {m.materialLabel}
              </span>
              <PriceInputCell
                {...m}
                onSave={(cents) => onSave(group.serviceCategoryId, m.materialOptionId, cents)}
                onRestore={onRestore ? () => onRestore(group.serviceCategoryId, m.materialOptionId) : undefined}
                saving={saving}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
